import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Grid, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { MdAdd } from 'react-icons/md';
import { AuthContext } from '../context/AuthProvider';
import { DOMAIN_PAGE } from '../utils/commonConstants';
import SearchAndFilter from '../reusable-component/SearchAndFilter';
import ClientNoData from '../components/Client/ClientNoData';
import DomainTable from '../components/dashboard-component/DomainTable';
import AddDomainModal from '../reusable-component/AddDomainModal';
import AccessModal from '../reusable-component/AccessModal';
import LoadingIcon from '../reusable-component/LoadingIcon';
import {
  getPaginatedDomainsApi,
  getAllFavourites,
  addDomainToFavourite,
  removeDomainFromFavourite,
} from "../api's/DomainApi";

const DomainPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { userRole } = useContext(AuthContext);
  const rowsPerPage = 5;
  // const isTabInitialized = useRef(false);
  const [tabValue, setTabValue] = useState(() => {
    const storedTab = sessionStorage.getItem('domain_tab_value');
    return storedTab !== null ? parseInt(storedTab, 10) : 0;
  });
  const [search, setSearch] = useState(sessionStorage.getItem('filtered_domain_search') || '');
  const [status, setStatus] = useState(sessionStorage.getItem('filtered_domain_status') || '');
  const [page, setPage] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [favouriteDomains, setFavouriteDomains] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openAccessModal, setOpenAccessModal] = useState(false);

  const debounceTimer = useRef(null);

  const fetchData = useCallback(
    async (pageNum = 0, currentSearch = search, currentStatus = status) => {
      setLoading(true);
      try {
        const payload = {
          page: pageNum,
          size: rowsPerPage,
          domainName: currentSearch || null,
          ...(tabValue === 0 && { status: currentStatus.toLowerCase() || null }),
        };

        const response = tabValue === 0 ? await getPaginatedDomainsApi(payload) : await getAllFavourites(payload);

        if (response.status === 200) {
          const { domainData = [], totalData = 0 } = response.data;
          tabValue === 0 ? setTableData(domainData) : setFavouriteDomains(domainData);
          setTotalCount(totalData);
        }
      } catch (err) {
        enqueueSnackbar('Failed to load domains', { variant: 'error' });
        tabValue === 0 ? setTableData([]) : setFavouriteDomains([]);
      } finally {
        setLoading(false);
      }
    },
    [tabValue, search, status]
  );

  const debouncedFetch = useCallback(
    (pageNum = 0, currentSearch = search, currentStatus = status) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchData(pageNum, currentSearch, currentStatus);
      }, 500);
    },
    [fetchData, search, status]
  );

  const handleSearchInput = (e) => {
    const newSearch = e.target.value;
    sessionStorage.setItem('filtered_domain_search', newSearch);
    setSearch(newSearch);
    setPage(0);
    debouncedFetch(0, newSearch, status);
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    sessionStorage.setItem('filtered_domain_status', newStatus);
    setStatus(newStatus);
    setPage(0);
    fetchData(0, search, newStatus);
  };

  const handleClearFilter = () => {
    const isSearchApplied = !!search;
    const isStatusApplied = !!status;
    if (!isSearchApplied && !isStatusApplied) return;
    sessionStorage.removeItem('filtered_domain_search');
    sessionStorage.removeItem('filtered_domain_status');
    setSearch('');
    setStatus('');
    setPage(0);
    fetchData(0, '', '');
  };

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
    sessionStorage.setItem('domain_tab_value', newValue);
    setPage(0);
  };

  useEffect(() => {
    fetchData(0, search, status);
  }, [tabValue]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(newPage, search, status);
  };

  const toggleFavorite = async (domainId) => {
    try {
      const isFav = favouriteDomains.some((d) => d.domain_id === domainId);
      const apiCall = isFav ? removeDomainFromFavourite : addDomainToFavourite;
      const response = await apiCall(domainId);
      if (response.status === 200) {
        enqueueSnackbar(isFav ? 'Removed from favorites' : 'Added to favorites', { variant: 'success' });
        if (tabValue === 1) fetchData(page);
      }
    } catch {
      enqueueSnackbar('Failed to update favorites', { variant: 'error' });
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchData(0, search, status);
  };

  const filterData = {
    search,
    status,
    handleSearch,
    handleOnChange: handleSearchInput,
    handleStatus: handleStatusChange,
    handleClearFilter,
    secondFilterText: DOMAIN_PAGE.domainFilterByStatus,
    clearFilterText: DOMAIN_PAGE.domainClearFilterButton,
    secondFilterValues: {
      filterData: ['Approved', 'Rejected', 'Pending'],
    },
    isFirstFilterVisible: false,
    isSecondFilterVisible: tabValue === 0||tabValue===1,
    isThirdFilterVisible: false,
  };

  return (
    <Box>
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{DOMAIN_PAGE.domainPageTitle}</Typography>
        <Button
          variant="contained"
          sx={{ backgroundColor: '#00043C' }}
          onClick={() =>
            userRole === 'READER' || userRole === 'EXECUTOR' ? setOpenAccessModal(true) : setOpenModal(true)
          }
        >
          <MdAdd /> {DOMAIN_PAGE.addNewDomainButton}
        </Button>
      </Grid>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          key={tabValue} 
          value={tabValue}
          onChange={handleTabChange}
          TabIndicatorProps={{ style: { backgroundColor: '#47D7AC' } }}
          sx={{ '& .MuiTab-root.Mui-selected': { color: '#00043C' } }}
        >
          <Tab label="All" sx={{ fontWeight: 'bold' }} />
          <Tab label="Favourite" sx={{ fontWeight: 'bold' }} />
        </Tabs>
      </Box>

      <SearchAndFilter data={filterData} />

      <Box mt={2}>
        {loading ? (
          <LoadingIcon />
        ) : (tabValue === 0 ? tableData : favouriteDomains).length > 0 ? (
          <>
            <DomainTable
              tableData={tabValue === 0 ? tableData : favouriteDomains}
              currentPage={page}
              search={search}
              totalCount={totalCount}
              setPage={setPage}
              handleChangePage={handleChangePage}
              toggleFavouriteDomain={toggleFavorite}
              fetchTableData={fetchData}
            />
          </>
        ) : (
          <Paper elevation={3} sx={{ height: 350 }}>
            <ClientNoData
              title={DOMAIN_PAGE.domainPageNoDataToDisplay}
              text={tabValue === 0 ? DOMAIN_PAGE.noDomainToReview : 'You have no favorite domains. Browse all domains and mark them as favourite.'}
              addBtnText={tabValue === 1 ? 'Browse Domains' : undefined}
              handleOnClick={tabValue === 1 ? () => setTabValue(0) : undefined}
              isAddBtn = {tabValue === 1}
            />
          </Paper>
        )}
      </Box>

      <AddDomainModal openModal={openModal} handleCloseModal={() => setOpenModal(false)} fetchData={fetchData} />
      <AccessModal openAccessModal={openAccessModal} handleAccessModal={() => setOpenAccessModal(false)} />
    </Box>
  );
};

export default DomainPage;
